import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { getTripJourneyDates } from '../../../api/trips/get-journeys-dates'
import { unpublishTripsJourneys } from '../../../api/trips/un-publish-trips-journeys'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ActionButtons from '../../elements/action-buttons'

type Props = {
  className?: string
  trips: number[]
  onCancel: () => void
}

const UnpublishJourneyModal = ({ className, trips, onCancel }: Props) => {
  const [selectedDates, setSelectedDates] = useState<number[]>([])

  const { data, isPending } = useQuery({
    queryKey: ['journeys-dates', ...trips],
    queryFn: () => getTripJourneyDates(trips),
    enabled: trips.length > 0
  })

  const unpublishJourneysMutation = useMutation({
    mutationFn: unpublishTripsJourneys,
    onSuccess: () => {
      toast.success('Les voyages sélectionnés ont été dépubliés avec succès.')
      onCancel()
    },
    onError: showAxiosError
  })

  if (isPending) return <Loader center />

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Vous êtes sur le point de dépublier un voyage. Merci de sélectionner les dates concernées.
      </Typography>
      {data && data.length > 0 ? (
        data.map((date) => (
          <div key={date.id} className="flex flex-col gap-1">
            <div className="flex item-center gap-1.5">
              <Checkbox
                checked={selectedDates.includes(date.id)}
                onChange={() => {
                  if (selectedDates.includes(date.id)) {
                    setSelectedDates(selectedDates.filter((id) => id !== date.id))
                  } else {
                    setSelectedDates([...selectedDates, date.id])
                  }
                }}
              />
              <Badge
                size="small"
                text={
                  <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                    {DateFormatter.DDMMYYYY(date.departureDate)} -{' '}
                    {DateFormatter.DDMMYYYY(date.returnDate)}
                  </Typography>
                }
              />
            </div>
          </div>
        ))
      ) : (
        <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
          Aucune date disponible
        </Typography>
      )}

      <ActionButtons
        cancelLabel="Annuler"
        isLoading={unpublishJourneysMutation.isPending}
        onCancel={onCancel}
        onSubmit={() =>
          unpublishJourneysMutation.mutate({ dateIds: selectedDates, tripIds: trips })
        }
      />
    </div>
  )
}

export default UnpublishJourneyModal
